import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/system/ui-components";

import { LoadingButton } from "../../components/LoadingButton";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";

interface DocumentUploadFooterProps {
  status: WorkerRequirementStatus;
  resubmissionDisabledFor?: { statuses: WorkerRequirementStatus[] };
  isLoading?: boolean;
  /**
   * Callback for the upload button.
   * Won't show if the status is IN_REVIEW.
   */
  onUploadButtonClick?: () => void;
  uploadButtonText?: string;
  replaceUploadButtonText?: string;
  /**
   * Callback for the sign button.
   * Won't show if the status is IN_REVIEW.
   */
  onSignButtonClick?: () => void;
  signButtonText?: string;
  replaceSignButtonText?: string;
  /**
   * Callback for the delete button.
   * Only shown if the status is IN_REVIEW.
   */
  onDeleteButtonClick?: () => void;
  children?: React.ReactNode;
}

export function DocumentUploadFooter(props: DocumentUploadFooterProps) {
  const {
    status,
    resubmissionDisabledFor,
    onUploadButtonClick,
    onSignButtonClick,
    uploadButtonText,
    signButtonText,
    replaceSignButtonText,
    onDeleteButtonClick,
    isLoading = false,
    replaceUploadButtonText,
    children,
  } = props;

  if (isDefined(resubmissionDisabledFor) && resubmissionDisabledFor.statuses.includes(status)) {
    return null;
  }

  if (status === WorkerRequirementStatus.IN_REVIEW) {
    return (
      <PageFooterWrapper>
        <LoadingButton
          fullWidth
          size="large"
          variant="outlined"
          isLoading={isLoading}
          onClick={onDeleteButtonClick}
        >
          Delete document
        </LoadingButton>
      </PageFooterWrapper>
    );
  }

  const buttonVariant = status === WorkerRequirementStatus.APPROVED ? "outlined" : "contained";
  const hasMultipleButtons = isDefined(onUploadButtonClick) && isDefined(onSignButtonClick);
  const isReplacement = [
    WorkerRequirementStatus.EXPIRING,
    WorkerRequirementStatus.APPROVED,
  ].includes(status);

  return (
    <PageFooterWrapper>
      <Stack spacing={4} sx={{ alignItems: "center" }}>
        {isDefined(onSignButtonClick) && (
          <LoadingButton
            fullWidth
            size="large"
            variant={buttonVariant}
            isLoading={isLoading}
            onClick={onSignButtonClick}
          >
            {isReplacement
              ? (replaceSignButtonText ?? "Sign replacement document")
              : (signButtonText ?? "Sign Document")}
          </LoadingButton>
        )}

        {hasMultipleButtons && (
          <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            or
          </Text>
        )}

        {isDefined(onUploadButtonClick) && (
          <LoadingButton
            fullWidth
            size="large"
            variant={buttonVariant}
            isLoading={isLoading}
            onClick={onUploadButtonClick}
          >
            {isReplacement
              ? (replaceUploadButtonText ?? "Upload replacement document")
              : (uploadButtonText ?? "Upload Document")}
          </LoadingButton>
        )}
      </Stack>
      {children}
    </PageFooterWrapper>
  );
}
